"use client";

import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useStorageQuery } from "../../hooks/storageQueryProvider";
import { t } from "@/lib/localization";

export default function DeleteFolderModal() {
    const { deleteFolderModalOpen, setDeleteFolderModalOpen } =
        useStorageQuery();

    const [isPending, setIsPending] = useState(false);

    const queryClient = useQueryClient();

    const closeModal = () => {
        setDeleteFolderModalOpen(null);
    };

    const submitHandler = async () => {
        const folderId = deleteFolderModalOpen?.id;

        if (!folderId) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
            return;
        }

        setIsPending(true);

        const res = await client.api.storage.folder[":folderId"].$delete({
            param: {
                folderId,
            },
        });

        if (!res.ok) {
            const error = await res.text();
            console.log(error);
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
        } else {
            const data = await res.json();
            console.log(data);

            queryClient.invalidateQueries({
                queryKey: ["get-storage-folders"],
            });
            queryClient.invalidateQueries({
                queryKey: ["get-storage-files"],
            });

            toast.success(t("toast.success.delete-folder"), {
                keyName: "toast.success.delete-folder",
            });
            setIsPending(false);
            closeModal();
        }
    };

    return (
        <AlertDialog
            open={!!deleteFolderModalOpen}
            onOpenChange={(open: boolean) => {
                if (!open) closeModal();
            }}
        >
            <AlertDialogContent className="sm:max-w-[380px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t("storage.delete-folder-modal.header.title", undefined, undefined)}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-foreground">
                        <p>
                            {t(
                                "storage.delete-folder-modal.body.confirm",
                                undefined,
                                {
                                    folderName:
                                        deleteFolderModalOpen?.name || "",
                                }
                            )}
                        </p>
                        <br />
                        <p>
                            {t(
                                "storage.delete-folder-modal.body.multi-folder-warning"
                            )}
                        </p>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-row gap-3">
                    <Button
                        disabled={isPending}
                        onClick={() => closeModal()}
                        variant="outline"
                        className="flex justify-center items-center"
                    >
                        {t("storage.delete-folder-modal.cancel-btn", undefined, undefined)}
                    </Button>
                    <Button
                        disabled={isPending}
                        onClick={() => submitHandler()}
                        variant="destructive"
                        className="flex justify-center items-center"
                    >
                        {isPending ? (
                            <LoaderCircle className="size-4 text-white animate-spin" />
                        ) : (
                            t("storage.folder.delete-folder-modal.delete-btn", undefined, undefined)
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
